import { create } from "zustand"

export interface AgencyData {
	abtaNumber: string
	phoneNumber: string
	agencyId: string
	agencyName: string
	approvalStatusCode: string
	agencyEmail: string
	allowNewRegistration: boolean
	internalAgencyFlag: boolean
	address: {
		cityName: string
		line1Addr: string
		line2Addr: string
		line3Addr: string
		line4Addr: string
		postalCode: string
		countryCode: string
		stateCode: string
	}
}

interface RegistrationState {
	firstName: string
	lastName: string
	emailAddress: string
	password: string
	confirmPassword: string
	mobilePhone: string
	cliaAgentId: string
	colonialID: string
	colonialIdValidation: {
		isValid: boolean | null
		message: string
		isValidating: boolean
	}
	espressoLanguage: string
	emailPreference: boolean | undefined
	mobilePreference: boolean
	acceptTermsConditions: boolean
	acceptTravelAgentGuideliness: boolean
	regions: string[]
	specialization1: string[]
	specialization2: string[]
	specialization3: string[]
	promotionBrand: string[]
	homeBasedAgent: boolean
	jobTitle: string
	yearsIndustry: string
	countryCode: string | ""
	zipCode: string
	isMobilePhoneValidated: boolean
	primaryMFA: string
	secondaryMFA: string
	agencyRole: string
	agencyBusinessModel: string
	consortialOrHost: boolean
	consortialOrHostName: string
	agencyData: AgencyData | null
	profilePictureURL: string
	profilePictureFile: File | null
	telephoneCountryCode: string
	telephoneValidationToken: string
	mailValidationToken: string
	abtaNumber: string
	telephoneNumber: string
	identificationType: string
	setProfilePictureURL: (profilePictureURL: string) => void
	setProfilePictureFile: (profilePictureFile: File | null) => void
	setFirstName: (firstName: string) => void
	setLastName: (lastName: string) => void
	setEmailAddress: (emailAddress: string) => void
	setPassword: (password: string) => void
	setConfirmPassword: (confirmPassword: string) => void
	setMobilePhone: (mobilePhone: string) => void
	setCliaAgentId: (cliaAgentId: string) => void
	setColonialID: (colonialID: string) => void
	setColonialIdValidation: (validation: {
		isValid: boolean | null
		message: string
		isValidating: boolean
	}) => void
	setEspressoLanguage: (espressoLanguage: string) => void
	setEmailPreference: (emailPreference: boolean) => void
	setMobilePreference: (mobilePreference: boolean) => void
	setAcceptTermsConditions: (acceptTermsConditions: boolean) => void
	setAcceptTravelAgentGuideliness: (
		acceptTravelAgentGuideliness: boolean
	) => void
	setRegions: (regions: string[]) => void
	setSpecialization1: (specialization1: string[]) => void
	setSpecialization2: (specialization2: string[]) => void
	setSpecialization3: (specialization3: string[]) => void
	setPromotionBrand: (promotionBrand: string[]) => void
	setHomeBasedAgent: (homeBasedAgent: boolean) => void
	setJobTitle: (jobTitle: string) => void
	setYearsIndustry: (yearsIndustry: string) => void
	setCountryCode: (countryCode: string | "") => void
	setZipCode: (zipCode: string) => void
	setIsMobilePhoneValidated: (isMobilePhoneValidated: boolean) => void
	setPrimaryMFA: (primaryMFA: string) => void
	setSecondaryMFA: (secondaryMFA: string) => void
	setAgencyRole: (agencyRole: string) => void
	setAgencyBusinessModel: (agencyBusinessModel: string) => void
	setConsortialOrHost: (consortialOrHost: boolean) => void
	setConsortialOrHostName: (consortialOrHostName: string) => void
	setAgencyData: (data: AgencyData | null) => void
	setTelephoneCountryCode: (telephoneCountryCode: string) => void
	setTelephoneValidationToken: (telephoneValidationToken: string) => void
	setMailValidationToken: (mailValidationToken: string) => void
	setAbtaNumber: (abtaNumber: string) => void
	setTelephoneNumber: (telephoneNumber: string) => void
	setIdentificationType: (identificationType: string) => void
	resetStore: () => void
}

const initialState = {
	firstName: "",
	lastName: "",
	emailAddress: "",
	password: "",
	confirmPassword: "",
	mobilePhone: "",
	cliaAgentId: "",
	colonialID: "",
	colonialIdValidation: {
		isValid: null,
		message: "",
		isValidating: false
	},
	espressoLanguage: "en_us" as const,
	emailPreference: undefined,
	mobilePreference: false,
	acceptTermsConditions: false,
	// TODO: Temporary - Travel Agent Guidelines checkbox is hidden for this release
	// Restore: acceptTravelAgentGuideliness: false,
	acceptTravelAgentGuideliness: true,
	regions: [],
	specialization1: [],
	specialization2: [],
	specialization3: [],
	promotionBrand: [],
	homeBasedAgent: false,
	jobTitle: "",
	yearsIndustry: "",
	countryCode: "",
	zipCode: "",
	isMobilePhoneValidated: false,
	primaryMFA: "",
	secondaryMFA: "",
	agencyRole: "",
	agencyBusinessModel: "",
	consortialOrHost: false,
	consortialOrHostName: "",
	agencyData: null as AgencyData | null,
	profilePictureURL: "",
	profilePictureFile: null as File | null,
	telephoneCountryCode: "",
	telephoneValidationToken: "",
	mailValidationToken: "",
	abtaNumber: "",
	telephoneNumber: "",
	identificationType: "phone"
}

const useRegistrationStore = create<RegistrationState>((set) => ({
	...initialState,
	// Setters
	setFirstName: (firstName) => set({ firstName }),
	setLastName: (lastName) => set({ lastName }),
	setEmailAddress: (emailAddress) => set({ emailAddress }),
	setPassword: (password) => set({ password }),
	setConfirmPassword: (confirmPassword) => set({ confirmPassword }),
	setMobilePhone: (mobilePhone) => set({ mobilePhone }),
	setCliaAgentId: (cliaAgentId) => set({ cliaAgentId }),
	setColonialID: (colonialID) => set({ colonialID }),
	setColonialIdValidation: (validation) =>
		set({ colonialIdValidation: validation }),
	setEspressoLanguage: (espressoLanguage) => set({ espressoLanguage }),
	setEmailPreference: (emailPreference) => set({ emailPreference }),
	setMobilePreference: (mobilePreference) => set({ mobilePreference }),
	setAcceptTermsConditions: (acceptTermsConditions) =>
		set({ acceptTermsConditions }),
	setAcceptTravelAgentGuideliness: (acceptTravelAgentGuideliness) =>
		set({ acceptTravelAgentGuideliness }),
	setRegions: (regions) => set({ regions }),
	setSpecialization1: (specialization1) => set({ specialization1 }),
	setSpecialization2: (specialization2) => set({ specialization2 }),
	setSpecialization3: (specialization3) => set({ specialization3 }),
	setPromotionBrand: (promotionBrand) => set({ promotionBrand }),
	setHomeBasedAgent: (homeBasedAgent) => set({ homeBasedAgent }),
	setJobTitle: (jobTitle: string) => set({ jobTitle }),
	setYearsIndustry: (yearsIndustry: string) => set({ yearsIndustry }),
	setCountryCode: (countryCode: string | "") => set({ countryCode }),
	setZipCode: (zipCode) => set({ zipCode }),
	setIsMobilePhoneValidated: (isMobilePhoneValidated: boolean) =>
		set({ isMobilePhoneValidated }),
	setPrimaryMFA: (primaryMFA: string) => set({ primaryMFA }),
	setSecondaryMFA: (secondaryMFA: string) => set({ secondaryMFA }),
	setAgencyRole: (agencyRole: string) => set({ agencyRole }),
	setAgencyBusinessModel: (agencyBusinessModel: string) =>
		set({ agencyBusinessModel }),
	setConsortialOrHost: (consortialOrHost: boolean) =>
		set({
			consortialOrHost,
			...(!consortialOrHost && { consortialOrHostName: "" })
		}),
	setConsortialOrHostName: (consortialOrHostName: string) =>
		set({ consortialOrHostName }),
	setAgencyData: (data: AgencyData | null) => set({ agencyData: data }),
	setProfilePictureURL: (profilePictureURL: string) =>
		set({ profilePictureURL }),
	setProfilePictureFile: (profilePictureFile: File | null) =>
		set({ profilePictureFile }),
	setTelephoneCountryCode: (telephoneCountryCode: string) =>
		set({ telephoneCountryCode }),
	setTelephoneValidationToken: (telephoneValidationToken: string) =>
		set({ telephoneValidationToken }),
	setMailValidationToken: (mailValidationToken: string) =>
		set({ mailValidationToken }),
	setTelephoneNumber: (telephoneNumber: string) => set({ telephoneNumber }),
	setAbtaNumber: (abtaNumber: string) => set({ abtaNumber }),
	setIdentificationType: (identificationType: string) =>
		set({ identificationType }),
	resetStore: () => set(initialState)
}))

export default useRegistrationStore
